/* eslint-disable @typescript-eslint/naming-convention */
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { DentalHistory, MedicalConditions, MedicalHistory } from '@brands/services/clinical_data/types/types';
import { UserProfile } from '@brands/services/identity/types/UserProfile';

export const checkEssentialProfileForm = (userInfo: UserProfile): boolean => {
  const basicInfo = userInfo?.basic_info;

  return !!(basicInfo?.first_name && basicInfo?.last_name && basicInfo?.date_of_birth);
};

export const checkProfileForm = (userInfo: UserProfile, selectedPatient: UserProfile | null): boolean => {
  const userProfileInfo = selectedPatient ?? userInfo;
  const basicInfo = userProfileInfo?.basic_info;
  const address = userProfileInfo?.address;

  // Dependents share the guardian's phone/email (they have no login of
  // their own); when a dependent is selected, check those two contact
  // fields on the guardian (userInfo) instead of on the dependent record.
  // Collector-created Patient users and self/primary Patients don't have
  // a guardian_id, so their contact info continues to be read from their
  // own record — preserves the DO-3923 fix for the Collector flow while
  // unbreaking the Patient-selecting-dependent flow (DO-3938).
  const isDependent = !!selectedPatient?.guardian_id;
  const contactRecord = isDependent ? userInfo : userProfileInfo;

  return !!(
    basicInfo?.first_name &&
    basicInfo?.last_name &&
    basicInfo?.gender &&
    basicInfo?.gender_identity &&
    basicInfo?.date_of_birth &&
    basicInfo?.languages &&
    basicInfo?.languages?.length > 0 &&
    contactRecord?.phone_number &&
    contactRecord?.email &&
    address?.address1 &&
    address?.city &&
    address?.state &&
    address?.zip_code
  );
};

export const checkMedicalHistoryForms = (medical_history: MedicalHistory): boolean => {
  if (medical_history && medical_history.height && medical_history.weight) {
    return true;
  }
  return false;
};

export const checkMedicalConditionsForms = (medical_conditions: MedicalConditions): boolean => {
  if (medical_conditions) {
    const containsYesOrNonEmptyString = Object.values(medical_conditions).some(
      (value) => value === 'yes' || (typeof value === 'string' && value.trim() !== '' && value !== 'no'),
    );
    if (containsYesOrNonEmptyString) {
      return true;
    }
  }
  return false;
};

export const checkDentalHistoryForms = (dental_history: DentalHistory): boolean => {
  if (
    dental_history &&
    (dental_history.last_dental_visit === 'dontKnow' ||
      (dental_history.visit_type !== 'no' && dental_history.who_did_you_see !== ''))
  ) {
    return true;
  }
  return false;
};

export const checkExternalFormAnswers = async (
  userInfo: UserProfile,
  selectedPatient: UserProfile | null,
  userRole?: string,
): Promise<boolean[]> => {
  const { medical_history, medical_conditions, dental_history } = await listClinicalData(
    selectedPatient?.id || userInfo.id,
    userRole,
  );
  const checkProfileFormAnswer = await checkProfileForm(userInfo, selectedPatient);
  const checkMedicalHistoryFormAnswer = await checkMedicalHistoryForms(medical_history);
  const checkMedicalConditionsFormAnswer = await checkMedicalConditionsForms(medical_conditions);
  const checkDentalHistoryFormAnswer = await checkDentalHistoryForms(dental_history);
  const checkEssentialProfileFormAnswer = await checkEssentialProfileForm(userInfo);

  return [
    checkProfileFormAnswer,
    checkMedicalHistoryFormAnswer,
    checkMedicalConditionsFormAnswer,
    checkDentalHistoryFormAnswer,
    checkEssentialProfileFormAnswer,
  ];
};
